import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Container, Grid, IconButton, Tab, Typography } from '@mui/material';
import { Add, CheckSharp, PlayArrowSharp } from '@mui/icons-material';
import IconRequired from 'src/assets/icon_required.svg';
import produce from 'immer';
import _, { isArray, isEmpty, isObject } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { CarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/CarePlan';
import { Communication } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Communication';
import { ServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/ServiceRequest';
import { CommunicationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Communication';
import { DocumentReferenceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import {
  useCommunications,
  useDocumentReferences,
  usePractitionerRoles,
  useImmunizations,
  usePatient,
  useRelatedPersons,
  useServiceRequest,
  useTasks,
  useObservations,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { PaginateQuery } from 'src/api/pagination/dtos';
import ExpandableCard from 'src/components/common/ExpandableCard';
import ComplementaryInfo from 'src/components/complementary-info/ComplementaryInfo';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import {
  AssessmentsFormsResponse,
  ReferralContactAttemptResponse,
  ReferralService,
} from 'src/crs/referral/services/ReferralService';
import useHealthCareServicesByServiceRequests from 'src/hooks/useHealthCareServicesByServiceRequests';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import { Annotation, Identifier } from 'src/careflow/ph-core/fhir-client/resources';
import PatientSummary from 'src/careflow/care-coordination/patient/detail/components/PatientSummary';
import {
  ConsentForm,
  ContactForm,
  InsuranceInfo,
  MissingNotes,
  ReferralHistory,
  ReferralSummary,
} from 'src/careflow/care-coordination/referral/';
import AddressInCountyDialog from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddressInCountyDialog';
import FixAddressDialog from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/FixAddressDialog';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { useDialogStore } from 'src/stores/dialog';
import {
  getAddressFormated,
  getFhirIdFromEntity,
  mergeLineAddress,
  getValuesFromReference,
} from 'src/utils/fhir';
import { fToNow, parseShortFormat } from 'src/utils/formatTime';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import {
  checkAclValidation,
  getRelatedAcls,
} from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { wait } from 'src/utils/timers';
import {
  getParseColumn,
  mapDataAndDataSchemaWorkflowInstance,
} from 'src/careflow/ph-core/workflow/helpers/util';
// import agent from '../../../../api/agent';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { RelatedPersonWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import {
  ConsentWrapper,
  WrappedConsent,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Consent';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { CareTeamMemberWithEditableData } from 'src/@types/crs/case';
import { ResourceWithIncludedResources } from 'src/careflow/helpers/types';
import { createTask } from '../../task/main/task.api';
import ConfirmGuarantorInfo from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ConfirmGuarantorInfo';
import ConfirmMemberInfo from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ConfirmMemberInfo';
import ConsentInformation, {
  getSelectOptionCustomButton as getConsentOption,
} from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ConsentInformation';
import ProviderInformation, {
  getSelectOptionCustomButton,
  PayloadProvider,
  workflowInstanceDataProvider,
} from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/ProviderInformation';
import {
  getConsents,
  mapActivityDefinitionTask,
  mapFhirResourceReference,
  buildMappingChecklistItem,
  handlerGenericStepWorkflow,
  workflowInstanceDataStep,
} from 'src/careflow/ph-core/workflow/helpers/workflow';
import { filterTasksByDefinition, translateTableHead } from 'src/careflow/helpers/common';
import { TableCustom } from '../../../../components/TableCustom';
import { NotesDisplayWithAdd, CRS_NOTES_COLUMNS } from 'src/components/NotesDisplay';
import ReferralWorkflowComp, { WorkflowData } from 'src/careflow/ph-core/workflow/Workflow';
import { CaseSelectionDialog } from '../../patient/cases/CaseSelectionDialog';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import {
  mapAssessmentsForms,
  mapCaseToCasesDisplay,
  mapCommunicationsToConsentFormDisplay,
  mapCommunicationsToNotesDisplay,
  mapContactAttemptsReferral,
  mapImmunizationsToDisplay,
} from 'src/careflow/common/common-utils';
import NoteAddForm from 'src/careflow/common/NoteAddForm';
import {
  STATUS_OPTION_IMMUNIZATION,
  TABLE_HEAD_ASSESSMENTS,
  TABLE_HEAD_CONSENT_FORM,
  TABLE_HEAD_CONTACT,
  TABLE_HEAD_IMMUNIZATIONS,
} from 'src/careflow/common/table-head';
import { DocumentList } from '../../patient/attachments/DocumentList';
import StartAssessmentsButton from '../../patient/assessment/list/StartAssessmentsButton';
import ExternalLink from './ExternalLink';
import ReferralIdentifiers from './ReferralIdentifiers';
import EditTaskDialog from './workflow-step/EditTaskDialog';
import { apiRoutes } from '../../../../utils/apiConfig';
import { fhirClient } from '../../../../App';
import { DeviceRequestsList } from '../../patient/devices/DeviceRequestsList';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import ConsentGrid from '../../patient/Consent/ConsentGrid';
import { CaseService } from 'src/crs/case/service/CaseService';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import ImmunizationAddForm from 'src/careflow/care-coordination/patient/immunization/ImmunizationAddForm';
import { assignOwnerHandler } from 'src/careflow/ph-core/workflow/handlers';
import { CREATE_TASK, UPDATE_TASK, useEventBus } from 'src/event-bus';
import TasksByPathway from 'src/careflow/care-coordination/task/patient/TasksByPathway';
import ObservationDetails from '../../patient/observations/ObservationDetails';
import AssessmentFromChecklistData from 'src/careflow/engagement-hub/patient-engagement/patient/components/workflow-step/AssessmentFromChecklistData';
import AssessmentFrame from '../../patient/assessment/frame/AssessmentFrame';
import {
  useTaskActivityDefinition,
  useTaskAssessment,
} from 'src/careflow/ph-core/workflow/helpers/hooks';
import { TaskWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { commonMenuTaskHandlers } from '../../task/common/handlers';
import { NonExistingInformation } from 'src/careflow/ph-core/workflow/components/common/ui/NonExistingInformation';
import { ObservationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { registerSteps } from 'src/careflow/ph-core/workflow/registry/workflowRegistry';

const getIdFromReference = (reference: string): string => reference.split('/')?.[1];

const ReferralDetails = memo(() => {
  const user = useAuth();
  const authUser = user?.getCurrentUser?.();

  const { configurations } = useTenantConfigData();

  const { themeStretch } = useSettings();

  const { enqueueSnackbar } = useSnackbar();

  const { confirmDialog, closeDialog } = useDialogStore();

  const [modals, setModals] = useState({
    immunization: false,
    note: false,
    orderDevice: false,
    setupDevice: false,
    fixAddress: false,
    addAddressTab: false,
    addressInCounty: false,
    insuranceInfo: false,
    missingProgressNotes: false,
    missingLabNotes: false,
    consentForm: false,
    contactClientForm: false,
    contactPCPForm: false,
    confirm: false,
    confirmGuarantor: false,
    consents: false,
    provideInformation: false,
    assessment: false,
    caseSelection: false,
  });

  const toggleModal = useCallback((key: keyof typeof modals, value: boolean) => {
    setModals((prev) => ({ ...prev, [key]: value }));
  }, []);

  const { id: referralId = null } = useParams();

  const [patientId, setPatientId] = useState<string | null>(null);

  const [immunizationsQuery, setImmunizationsQuery] = useState({});
  const [communicationsQuery, setCommunicationsQuery] = useState({});
  const [documentReferencesQuery, setDocumentReferencesQuery] = useState({});
  const [caseDataByPatient, setCaseDataByPatient] = useState<any | undefined | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);

  const [intakeOption, setIntakeOption] = useState<any>(null);

  const [checklistData, setChecklistData] = useState<any>({});
  const [contactAttempt, setContactAttempt] = useState<ReferralContactAttemptResponse[]>([]);
  const [assessmentsForms, setAssessmentsForms] = useState<AssessmentsFormsResponse[]>([]);

  const [assessmentsLinks, setAssessmentsLinks] = useState<any[]>([]);

  const [workflowStage, setWorkflowStage] = useState(undefined);
  const [referralRecord, setReferralRecord] = useState<any>({});
  const [workflowInstance, setWorkflowInstance] = useState<any>({});
  const [allUsers, setAllUsers] = useState([]);
  const [consents, setConsents] = useState<ResourceWithIncludedResources<WrappedConsent>[]>([]);
  const [refreshConsents, setRefreshConsents] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disposition, setDisposition] = useState(false);
  const [reject, setReject] = useState(false);
  const [rejectExternal, setRejectExternal] = useState(false);
  const [rejectCounty, setRejectCounty] = useState(false);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openCollapseReferralSummary, setOpenCollapseReferralSummary] = useState(true);
  const { i18n } = useLocales();
  const [initialWorkflow, setInitialWorkflow] = useState<any>(null);
  const [metadataWorkflow, setMetadataWorkflow] = useState<any>({});

  const [serviceRequestTasks, setServiceRequestTasks] = useState([]);
  const { onEvent, removeEvent } = useEventBus();

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const lastContactClient =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Contact Client').pop()
      ?.referralContactAttempContactOn;
  const countContactClient =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Contact Client').length;
  const lastContactPCP =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Document PCP').pop()
      ?.referralContactAttempContactOn;
  const countContactPCP =
    Array.isArray(contactAttempt) &&
    contactAttempt?.filter((e) => e.noteType === 'Document PCP').length;

  const buttonDataContactClient = `[${countContactClient}/5]
  ${
    !isEmpty(lastContactClient)
      ? `(Last on ${parseShortFormat(lastContactClient || '')} - 
  ${fToNow(lastContactClient || '')})`
      : ''
  }`;

  const buttonDataContactPCP = `[${countContactPCP}/5]
  ${
    !isEmpty(lastContactPCP)
      ? `(Last on ${parseShortFormat(lastContactPCP || '')} - 
  ${fToNow(lastContactPCP || '')})`
      : ''
  }`;

  const fetchAssessments = async (surveyId: any) => {
    const result: any = await ReferralService.assessments(surveyId, 'ServiceRequest');
    setAssessmentsForms(mapAssessmentsForms(result));
  };

  const fetchAssessmentsLinks = async () => {
    const result: any = await getRegistries(undefined, 'crs-patient-assessments');
    const data = result?.data;
    if (data && data[0]) {
      const JsonObject = JSON.parse(data[0]?.keyValue);
      setAssessmentsLinks(JsonObject);
    }
  };

  const fetchReferralRecord = async (id: any) => {
    if (!referralId) return;
    const result: any = await ReferralService.getOne(id);

    fetchAssessmentsLinks();
    setReferralRecord(result);
  };

  const onTransitionEnd = async () => {
    if (referralId) {
      console.log(`DEBUG onTransitionEnd referralId ${referralId}`);
      fetchReferralRecord(referralId);
    }
  };

  const fetchContactAttempt = async (fhirId: string) => {
    const result: any = await ReferralService.getContactAttempt(fhirId);
    setContactAttempt(result);
  };
  const getAllUsers = async () => {
    if (!workflowInstance?.scope) {
      return;
    }

    try {
      let workflowMetadata: any = {};
      let ownerACLs: string[] | null = null;

      if (workflowInstance?.workFlow?.metadata) {
        try {
          workflowMetadata = JSON.parse(workflowInstance.workFlow.metadata);
          ownerACLs = workflowMetadata?.workflow?.ownerAllowedACLs;
        } catch (parseError) {
          console.log('error parsing workflow metadata', parseError);
        }
      }

      let result;
      if (ownerACLs && Array.isArray(ownerACLs) && ownerACLs.length > 0) {
        // Use ACL-based filtering when configuration exists
        const relatedACLs = getRelatedAcls(ownerACLs);
        result = await UserService.getAllUsersByAcls(relatedACLs);
      } else {
        result = await UserService.getUsersByOrgAndRole(userOrganizations, []);
      }

      setAllUsers(result.filter((e: any) => e.status === 'Active'));
    } catch (err) {
      console.log('error on fetching all users', err);
    }
  };

  useEffect(() => {
    fetchAssessments(referralId);
    fetchReferralRecord(referralId);
    fetchContactAttempt(referralId || '');
  }, [referralId]);

  useEffect(() => {
    if (workflowInstance?.scope) {
      getAllUsers();
    }
  }, [workflowInstance?.scope]);

  /*
   ** Mock Data Workflow
   */
  // const REFERENCE_NAME = useMemo(() => referralRecord?.workflowScope, [referralRecord]);
  const REFERENCE_NAME = 'REFERRAL';

  // const INITIAL_WORKFLOW = useMemo(
  //   () => getWorkflowProperty(REFERENCE_NAME)?.code,
  //   [REFERENCE_NAME]
  // );

  useEffect(() => {
    fetchStageFromWorkflow();
  }, [referralRecord]);

  //REFERRALS_WORKFLOW handler's
  const createWorkflowHandler = useCallback(
    (modalKey: keyof typeof modals) =>
      ({ checklistItem }: any) => {
        toggleModal(modalKey, true);
        setChecklistData(checklistItem);
      },
    [toggleModal]
  );

  const validateHomeItemHandler = createWorkflowHandler('fixAddress');
  const validateAddressInCountyItemHandler = createWorkflowHandler('addressInCounty');
  const consentFormHandler = createWorkflowHandler('consentForm');
  const missingInsuranceHandler = createWorkflowHandler('insuranceInfo');
  const missingProgressNotesHandler = createWorkflowHandler('missingProgressNotes');
  const missingLabNotesHandler = createWorkflowHandler('missingLabNotes');
  const contactClientHandler = createWorkflowHandler('contactClientForm');
  const contactPCPHandler = createWorkflowHandler('contactPCPForm');
  const orderDeviceHandler = createWorkflowHandler('orderDevice');
  const setupDeviceHandler = createWorkflowHandler('setupDevice');
  const handleAssessment = createWorkflowHandler('assessment');
  const handlerConfirmMemberInfo = createWorkflowHandler('confirm');
  const handlerConfirmationGuarantorInfo = createWorkflowHandler('confirmGuarantor');
  const handlerConsents = createWorkflowHandler('consents');
  const handlerProviderInformation = createWorkflowHandler('provideInformation');

  const handlerConfirmCareTeam = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setChecklistData(checklistItem);
    const { resourceRelated, status, response: responseMore } = more;
    setIsLoading(true);
    try {
      if (status && resourceRelated) {
        const mappedTask = await mapActivityDefinitionTask(resourceRelated, patient, status);

        if (!mappedTask) {
          enqueueSnackbar(
            `${i18n(
              'patientEngagement.details.workflow.checklist.confirmCareTeam.notExistAD',
              'engagement'
            )}`,
            { variant: 'error' }
          );
          setIsLoading(false);
          return;
        }

        const response = await createTask(mappedTask);

        const modifiedFields = {
          fhirReference: mapFhirResourceReference(response),
          response: responseMore,
        };

        const payloadForUpdateWorkflowinstance = {
          referenceValue: referralId,
          referenceName: referralRecord?.workflowReferenceName,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          referralRecord,
          payloadForUpdateWorkflowinstance,
          checklistItem,
          authUser?.email,
          modifiedFields
        );
      } else {
        enqueueSnackbar(
          `${i18n(
            'patientEngagement.details.workflow.checklist.confirmCareTeam.checklistItemData',
            'engagement'
          )}`,
          { variant: 'error' }
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      fetchReferralRecord(referralId);
      fetchStageFromWorkflow();
      setIsLoading(false);
    }
  };

  //WORKFLOW common handler's
  const assignReferralItemHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    more,
  }: any) => {
    setChecklistData(checklistItem);
    await assignOwnerHandler({
      record: referralRecord,
      workflowInstance,
      userEmail: authUser?.email,
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
      checklistItem,
      checklistData: checklistItem,
      more,
      onError: () => {
        enqueueSnackbar('An error has occurred', { variant: 'error' });
        console.log('error on trying to assignReferralItemHandler');
      },
      onFinally: async () => {
        refreshChecklistHandler?.();
      },
    });
  };

  const fetchStageFromWorkflow = async () => {
    if (isEmpty(referralRecord)) return;
    try {
      const result = await WorkflowService.getInstance(
        referralId,
        referralRecord?.workflowReferenceName
      );
      setWorkflowInstance(result);
      setWorkflowStage(result?.stage?.id);
      setInitialWorkflow(result?.workFlow?.code);
      const components = result?.workFlow?.metadata
        ? JSON.parse(result?.workFlow?.metadata).components?.workflow
        : null;
      setMetadataWorkflow(components);
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleTransitionSuccess = useCallback(
    (scenario: 'next' | 'last', transition?: any) => {
      if (scenario === 'last') {
        //TODO this should be enabled based on the workflow or transaction.
        toggleModal('caseSelection', true);
      }
    },
    [toggleModal]
  );

  const handleCaseSelected = useCallback(
    async (selectedCase: any) => {
      // Determine the correct fhirId based on the type of selectedCase
      const fhirIdToUse = selectedCase?.fhirId || selectedCase?.id;

      if (!fhirIdToUse || !referralId) return;

      try {
        // Fetch the FHIR CarePlan resource using the determined fhirId
        const fhirCarePlan = await fhirClient.getResource<CarePlan>(`CarePlan/${fhirIdToUse}`);
        const supportingInfo = fhirCarePlan?.supportingInfo || [];
        const referralRef = `ServiceRequest/${referralId}`;

        if (!supportingInfo.some((ref: any) => ref.reference === referralRef)) {
          const updatedCarePlan = {
            ...fhirCarePlan,
            supportingInfo: [...supportingInfo, { reference: referralRef }],
          };
          await fhirClient.updateOne<CarePlan>('CarePlan', updatedCarePlan);
          enqueueSnackbar('Referral linked to case successfully', { variant: 'success' });
        }
      } catch (error) {
        console.error('Error linking referral to case:', error);
        enqueueSnackbar('Failed to link referral to case', { variant: 'error' });
      }
    },
    [referralId, enqueueSnackbar]
  );

  const referralWorkflowData: WorkflowData = useMemo(
    () => ({
      id: 1,
      recordId: referralId,
      recordReference: referralRecord?.workflowReferenceName,
      stage: {
        id: workflowStage,
      },
      referralRecord,
      workflowInstance,
    }),
    [workflowStage, workflowInstance, referralRecord]
  );

  const getIntakeOption = async () => {
    setIsLoading(true);
    const data = await getRegistries('intake', 'intake-option');
    const dataKeyValue = data?.[0]?.keyValue ? JSON.parse(data?.[0]?.keyValue) : null;
    setIntakeOption(dataKeyValue);
    setIsLoading(false);
  };

  useEffect(() => {
    getIntakeOption();
  }, []);

  const [patient, { update: updatePatient, refresh: refreshPatient }] = usePatient(patientId, {
    map: PatientWrapper,
  });

  const [observations] = useObservations({
    filter: {
      patient: patient?.id,
    },
    map: ObservationWrapper,
    autofetch: !!patient?.id,
  });

  const practitionerRolesId = useMemo(
    () =>
      patient?.generalPractitioner
        ?.filter(({ reference }) => reference?.includes('PractitionerRole'))
        .map(({ reference }) => {
          const [_, id] = getValuesFromReference(reference);
          return id;
        }),
    [patient]
  );

  const [
    practitionerRoles,
    { isFetching: isPractitionerRoleFetching, refresh: refreshPractitionerRoles },
  ] = usePractitionerRoles({
    filter: {
      _id: practitionerRolesId?.join(','),
      active: true,
    },
    map: PractitionerRoleWrapper,
    autofetch: !!practitionerRolesId?.length,
  });
  const userEmail = user?.getCurrentUser?.()?.email;

  const filteredPractitionerRoles = useMemo(() => {
    if (
      practitionerRoles &&
      isArray(practitionerRoles) &&
      practitionerRoles.length > 0 &&
      practitionerRolesId &&
      isArray(practitionerRolesId) &&
      practitionerRolesId.length > 0 &&
      patient?.generalPractitioner &&
      isArray(patient?.generalPractitioner) &&
      patient?.generalPractitioner.length > 0
    ) {
      const filtered = practitionerRoles.filter((x) => x?.id && practitionerRolesId.includes(x.id));
      return filtered;
    }
    return [];
  }, [practitionerRoles, practitionerRolesId, patient]);

  const relatedProviders = useMemo(() => {
    const combinedResources: CareTeamMemberWithEditableData[] = [
      // Note: careTeams variable needs to be populated via useCareTeams hook below
      ...(filteredPractitionerRoles
        ? filteredPractitionerRoles.map((practitionerRole) => ({
            id: practitionerRole.id ?? '',
            associatedOrg: '',
            associatedOrgName: '',
            associatedOrgId: null,
            role: practitionerRole?.code?.[0]?.coding?.[0]?.display ?? '',
            roleName: practitionerRole?.code?.[0]?.coding?.[0]?.display ?? '',
            roleId: practitionerRole?.code?.[0]?.coding?.[0]?.code ?? '',
            name: practitionerRole?.practitioner?.display ?? '',
            identifier: '',
            identifierType: '',
            memberType: practitionerRole.resourceType,
            startDate: '',
            endDate: '',
            startDateObj: null,
            endDateObj: null,
            versionId: practitionerRole.meta?.versionId ?? '',
          }))
        : []),
    ];
    return combinedResources;
  }, [filteredPractitionerRoles]);

  const providerInfoItemData = useMemo(() => {
    const { providers } = intakeOption || {};
    const workflowInstanceDataProviders = workflowInstanceDataProvider(
      getParseColumn(workflowInstance, 'data'),
      'notApplicable'
    );

    const data = getSelectOptionCustomButton(relatedProviders ?? [], providers ?? []);

    return data?.map((e) => {
      const workflowProvider = workflowInstanceDataProviders?.find(
        (provider) => provider.role === e.role
      );

      return {
        title: e?.sub,
        value: workflowProvider ? workflowProvider.title : e?.title ?? '',
      };
    });
  }, [relatedProviders, intakeOption, workflowInstance]);

  const consentInfoItemData = useMemo(() => {
    const { consents: consentRegistry } = intakeOption || {};
    const data = getConsentOption(consents, consentRegistry);
    return data?.map((e) => ({
      title: e?.title,
      value: e?.sub,
    }));
  }, [consents, intakeOption]);

  const dataMappingChecklistItem = useMemo(
    () => ({
      ...buildMappingChecklistItem('NEEDS_CARE_PLAN_ITEM', 'observations', observations, userEmail),
    }),
    [observations]
  );

  const confirmHandler = async ({
    record,
    checklistItem,
    refreshChecklistHandler,
    refreshRecordHandler,
    data,
  }: any) => {
    let workflowDataScope: any = null;
    let modifiedFields: any = null;

    if (data?.key && data?.resource) {
      workflowDataScope = buildMappingChecklistItem(
        checklistItem?.code,
        data.key,
        data.resource,
        userEmail
      );
      modifiedFields = { [data.key]: workflowDataScope };
    }

    const getData = dataMappingChecklistItem[checklistItem.code] ?? null;

    if (!isEmpty(getData?.[getData.key])) {
      const { key } = getData;
      const data = getData[key];

      const dataScope = workflowInstanceDataStep(
        workflowInstance,
        `${checklistItem.code}.modifiedFields.${key}`
      );

      if (Array.isArray(data)) {
        workflowDataScope = Array.isArray(dataScope) ? [...dataScope] : [];

        for (const newData of data) {
          const existingIndex = workflowDataScope?.findIndex(
            (item) => item?.fhirReference === newData?.fhirReference
          );

          if (existingIndex !== -1) {
            workflowDataScope[existingIndex] = newData;
          } else {
            workflowDataScope.push(newData);
          }
        }
      } else if (isObject(data)) {
        workflowDataScope = data;
      }

      modifiedFields = { [key]: workflowDataScope };
    }

    const metadata = checklistItem?.metadata && JSON.parse(checklistItem.metadata);
    const { allowNonExistingInformation = null, message = null } = metadata?.ui?.data ?? {};

    if (allowNonExistingInformation) {
      modifiedFields = { message: message };
    }

    if (isEmpty(workflowDataScope) && !allowNonExistingInformation) {
      await confirmDialog({
        key: 'this',
        render: () => <NonExistingInformation i18n={i18n} closeDialog={closeDialog} />,
      });
      return;
    }

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistItem ? checklistItem : checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      await fetchReferralRecord(referralId);
      await fetchStageFromWorkflow();
    }
  };

  // 1. Define independent, reusable workflow actions based on component types
  const BaseHandlers = {
    assignOwner: {
      type: 'payload',
      handler: assignReferralItemHandler,
      data: allUsers,
    },
    validateHomeAddress: {
      type: 'payload',
      handler: validateHomeItemHandler,
      data: true,
    },
    validateAddressInCounty: {
      type: 'payload',
      handler: validateAddressInCountyItemHandler,
      data: true,
    },
    addInsuranceInfo: {
      type: 'payload',
      handler: missingInsuranceHandler,
      data: true,
    },
    addProgressNotes: {
      type: 'payload',
      handler: missingProgressNotesHandler,
      data: true,
    },
    addLabNotes: {
      type: 'payload',
      handler: missingLabNotesHandler,
      data: true,
    },
    consentForm: {
      type: 'payload',
      handler: consentFormHandler,
      data: true,
    },
    contactClient: {
      type: 'payload',
      handler: contactClientHandler,
      data: buttonDataContactClient,
    },
    contactPcp: {
      type: 'payload',
      handler: contactPCPHandler,
      data: buttonDataContactPCP,
    },
    prepareDevice: {
      type: 'confirm',
      handler: orderDeviceHandler,
      data: true,
    },
    setupDevice: {
      type: 'confirm',
      handler: setupDeviceHandler,
      data: true,
    },
    completeAssessment: {
      type: 'payload',
      handler: handleAssessment,
      data: true,
    },
    confirmMemberInfo: {
      type: 'confirm',
      handler: handlerConfirmMemberInfo,
      data: true,
    },
    confirmGuarantorInfo: {
      type: 'confirm',
      handler: handlerConfirmationGuarantorInfo,
      data: true,
    },
    consentInformation: {
      type: 'payload',
      handler: handlerConsents,
      data: consentInfoItemData ? consentInfoItemData : true,
    },
    providerInformation: {
      type: 'payload',
      handler: handlerProviderInformation,
      data: providerInfoItemData ? providerInfoItemData : true,
    },
    confirmCareTeam: {
      type: 'payload',
      handler: handlerConfirmCareTeam,
      data: true,
    },

    // Workflow transition actions
    confirm: {
      type: 'confirm',
      handler: confirmHandler,
      data: false,
    },
    setDisposition: {
      type: 'confirm',
      handler: () => setDisposition(true),
      data: caseDataByPatient,
    },
    setReject: {
      type: 'confirm',
      handler: (open: boolean) => setReject(open),
      data: rejectCounty,
    },
    setRejectExternal: {
      type: 'confirm',
      handler: (open: boolean) => setRejectExternal(open),
      data: false,
    },
  };

  // 2. Construct the WorkflowHandler with the actions and map legacy codes
  const WorkflowHandler = {
    ...BaseHandlers,

    // Backward compatibility mappings for legacy hardcoded step codes
    ASSIGN_REFERRAL_ITEM: BaseHandlers.assignOwner,
    ASSIGN_OWNER_REMOTE_PATIENT_MONITORING_ITEM: BaseHandlers.assignOwner,
    VALIDATE_HOME_ADDRESS_REFERRAL_ITEM: BaseHandlers.validateHomeAddress,
    VALIDATE_ADDRESS_IN_COUNTY_REFERRAL_ITEM: BaseHandlers.validateAddressInCounty,
    MISSING_INSURANCE_INFO_REFERRAL_ITEM: BaseHandlers.addInsuranceInfo,
    MISSING_PROGRESS_NOTES_REFERRAL_ITEM: BaseHandlers.addProgressNotes,
    MISSING_LAB_NOTES_REFERRAL_ITEM: BaseHandlers.addLabNotes,
    CONSENT_FORM_REFERRAL_ITEM: BaseHandlers.consentForm,
    CONTACT_CLIENT_REFERRAL_ITEM: BaseHandlers.contactClient,
    CONTACT_PCP_REFERRAL_ITEM: BaseHandlers.contactPcp,
    ORDER_: BaseHandlers.contactPcp,
    PREPARE_DEVICE_REMOTE_PATIENT_MONITORING_ITEM: BaseHandlers.prepareDevice,
    SETUP_DEVICE_REMOTE_PATIENT_MONITORING_ITEM: BaseHandlers.setupDevice,
    CONFIRM_CARE_TEAM_INTAKE_ITEM: BaseHandlers.confirmCareTeam,
    PROVIDER_INFORMATION_INTAKE_ITEM: BaseHandlers.providerInformation,
  };

  const WorkflowData = {
    disposition,
    reject,
    rejectExternal,
  };

  const [
    serviceRequest,
    {
      isSuccess: isSuccessServiceRequest,
      refresh: refreshServiceRequest,
      update: updateServiceRequest,
    },
  ] = useServiceRequest(referralId, { map: ServiceRequestWrapper });

  const fhirPatientId = useMemo(
    () => referralRecord?.indexedFhirSubjectRefUri?.trim().slice('-36'),
    [referralRecord]
  );

  const [immunizations, { refresh: refreshImmunizations, create: createImmunization }] =
    useImmunizations({ filter: immunizationsQuery });

  const [communications, { refresh: refreshCommunications, create: createCommunication }] =
    useCommunications({
      filter: communicationsQuery,
      map: CommunicationWrapper,
    });

  const [
    documentReferences,
    { refresh: refreshDocumentReference, isFetching: isDocumentsLoading },
  ] = useDocumentReferences({
    filter: documentReferencesQuery,
    map: DocumentReferenceWrapper,
  });

  const { healthCareServices } = useHealthCareServicesByServiceRequests(
    referralId ? [referralId] : []
  );

  const [
    relatedPersons,
    { create: createRelatedPerson, update: updateRelatedPerson, refresh: refreshRelatedPerson },
  ] = useRelatedPersons({
    filter: {
      patient: patient?.id,
      active: true,
    },
    autofetch: !!patient,
    map: RelatedPersonWrapper,
  });

  const [tasks, { isLoading: isTasksLoading, refresh: refreshTaskList }] = useTasks({
    filter: {
      subject: patient?.id,
    },
    map: TaskWrapper,
  });

  const taskActivityDefinition = useTaskActivityDefinition();

  const taskFilterByActivityDefinition = useMemo(
    () => filterTasksByDefinition(tasks, taskActivityDefinition?.referral),
    [tasks, taskActivityDefinition]
  );

  registerSteps({
    ObservationDetails: () => (
      <Card>
        <ObservationDetails
          patient={patient}
          customQuery={{ patient: patient?.id }}
          showEmpty={!patient?.id}
          permissions={{
            isAllowedAdd: checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.OBSERVATION.ADD] }),
            isAllowedEdit: checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.OBSERVATION.EDIT] }),
          }}
        />
      </Card>
    ),
    ResourceReferral: (props: any) => {
      const { props: propOther = null } = props ?? {};
      const filters = propOther ? propOther?.filters : null;
      return (
        <TasksByPathway
          permissions={taskPermissions}
          taskActivityDefinitionContext={taskActivityDefinition?.caseGoal}
          tasks={tasks}
          patient={patient as WrappedPatient}
          healthCareServices={healthCareServices}
          serviceRequests={serviceRequest ? [serviceRequest] : []}
          menuTaskHandlers={commonMenuTaskHandlers}
          onAssessmentSelect={handleAssessmentSelect}
          filterTasksFromChecklistItem={filters}
          isWorkflowContext={true}
        />
      );
    },
    Tasks: (props: any) => {
      const { props: propOther = null } = props ?? {};
      const filters = propOther ? propOther?.filters : null;
      return (
        <TasksByPathway
          permissions={taskPermissions}
          taskActivityDefinitionContext={taskActivityDefinition?.case}
          tasks={taskFilterByActivityDefinition}
          patient={patient as WrappedPatient}
          healthCareServices={healthCareServices}
          serviceRequests={serviceRequest ? [serviceRequest] : []}
          menuTaskHandlers={commonMenuTaskHandlers}
          onAssessmentSelect={handleAssessmentSelect}
          filterTasksFromChecklistItem={filters}
          isWorkflowContext={true}
        />
      );
    },
    ContactAttempts: () => (
      <Card>
        <TableCustom
          data={Array.isArray(contactAttempt) && mapContactAttemptsReferral(contactAttempt)}
          tableHead={translateTableHead(TABLE_HEAD_CONTACT, i18n, 'crs')}
          childrenButtons={
            <Grid container display={'flex'} sx={{ my: 1, px: 1 }}>
              <Grid item xs={12} display={'flex'} justifyContent={'flex-end'}>
                {referralPermissions.isAllowedAddContactAttempt && (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ height: '36px' }}
                    startIcon={<Add />}
                    onClick={() => toggleModal('contactClientForm', true)}
                  >
                    {i18n('referral.details.contactAttempts.buttoncontactAttempts', 'crs')}
                  </Button>
                )}
              </Grid>
            </Grid>
          }
        />
      </Card>
    ),
    ContactAttemptTab: ({ itemName, isValid, checklistItem, isRequired, ...props }: any) => {
      const { selected } = props;
      useEffect(() => {
        if (selected && checklistItem) {
          setChecklistData(checklistItem);
        }
      }, [selected, checklistItem]);
      const icon = isValid ? <CheckSharp /> : isRequired ? <IconRequired /> : <PlayArrowSharp />;
      return <Tab {...props} icon={icon} iconPosition="start" label={itemName} />;
    },
  });

  useEffect(() => {
    const getConsentsAsync = async () => {
      const consents = await getConsents(patient);
      setConsents(consents || []);
      setRefreshConsents(false);
    };
    getConsentsAsync();
  }, [patient, refreshConsents]);

  useEffect(() => {
    if (serviceRequest && isSuccessServiceRequest) {
      if (serviceRequest?.subject?.reference) {
        setPatientId(getIdFromReference(serviceRequest?.subject?.reference));
      }
      setCommunicationsQuery({ ...communicationsQuery, 'part-of': serviceRequest.id });
      setDocumentReferencesQuery({ ...documentReferencesQuery, related: serviceRequest.id });
    }
  }, [serviceRequest, isSuccessServiceRequest]);

  useEffect(() => {
    if (patientId) {
      setImmunizationsQuery({ ...immunizationsQuery, patient: patientId });
      getCaseListByPatient();
    }
  }, [patientId]);

  const handleCreateImmunization = async (data: any) => {
    const result = await createImmunization(data);
    refreshImmunizations();
    enqueueSnackbar('Immunization created successfully!');
    toggleModal('immunization', false);
  };

  const handleMarkItemCompleted = async () => {
    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail
      );
    } catch (err) {
      console.log('error on handleInsuranceInfo markItemCompleted', err);
    } finally {
      setChecklistData({});
    }
  };

  const handleCreateCommunication = async (data: any) => {
    let result: any = [];

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      result = await handleCreateCommunicationFhir(data);
      const modifiedFields = {
        fhirReference: mapFhirResourceReference(result),
        source: 'workflow',
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error on handleCreateCommunication', err);
    } finally {
      toggleModal('consentForm', false);
      setChecklistData({});
    }

    return result;
  };

  const handleCreateCommunicationFhir = async (data: any) => {
    let result: any = {};

    try {
      result = await createCommunication(data);

      const setNote = result?.map((x1: Communication) => ({
        id: `${x1.resourceType}/${x1.id}`,
        text: x1?.payload?.[0]?.contentString || x1?.medium?.[0]?.coding?.[0]?.display,
      }));
      const note = serviceRequest?.note ?? [];
      const setNoteServiceRequest: Annotation[] = [...setNote, ...note];

      if (setNoteServiceRequest && serviceRequest) {
        serviceRequest.note = setNoteServiceRequest;
        handleUpdateReferral(serviceRequest);
      }

      toggleModal('note', false);
      refreshCommunications();
    } catch (err) {
      console.log('error on handleCreateCommunicationFhir', err);
    }

    return result;
  };

  const handleCreateContactForm = async (data: any) => {
    let resultContactAttempt: any = {};

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      resultContactAttempt = await ReferralService.createContactAttempt(data);
      if (!isEmpty(resultContactAttempt)) {
        const workflowDataScope =
          workflowInstanceDataStep(
            workflowInstance,
            `${checklistData.code}.modifiedFields.contacts`
          ) ?? [];

        workflowDataScope.push({
          outcome: resultContactAttempt?.outcome,
          contactOn: resultContactAttempt?.contactOn,
          nextContactOn: resultContactAttempt?.nextContactOn,
          reasonNote: data?.reasonNote ?? '',
        });

        const modifiedFields = {
          contacts: workflowDataScope,
        };

        await handlerGenericStepWorkflow(
          workflowInstance,
          referralRecord,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields
        );
      }
    } catch (err) {
      console.log('error on handleCreateContactForm markItemCompleted', err);
    } finally {
      fetchContactAttempt(referralId || '');
    }

    return resultContactAttempt;
  };

  const handleSyncReferral = async () => {
    setSyncing(true);
    try {
      await wait(1000);
      const data = await ReferralService.sync(serviceRequest?.id ?? '');
    } catch (error) {
      enqueueSnackbar('There was an error trying to sync referral. Please try again', {
        variant: 'error',
      });
      setSyncing(false);
      return;
    }
    enqueueSnackbar('Referral Synced Succesfully', {
      variant: 'success',
    });
    refreshServiceRequest();
    setSyncing(false);
  };

  const handleFixAddress = async (data: any) => {
    const address = patient?.address !== undefined ? [...patient?.address, data] : [data];

    try {
      await updatePatient(
        produce(patient!, (draft) => {
          draft.address = address;
        })
      );
      await refreshPatient();
      toggleModal('addAddressTab', false);
    } catch (err) {
      console.log('error on handleFixAddress', err);
    }
  };

  const saveSelectedAddress = async (address: any) => {
    const modifiedFields = {
      ...address,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      if (checklistData?.code !== 'VALIDATE_HOME_ADDRESS_REFERRAL_ITEM') {
        const { data: dataWorkflowInstance, dataSchema } = mapDataAndDataSchemaWorkflowInstance(
          workflowInstance,
          userEmail,
          modifiedFields,
          null,
          'VALIDATE_HOME_ADDRESS_REFERRAL_ITEM'
        );
        const payloadForUpdateWorkflowinstance = {
          referenceValue: referralId,
          referenceName: referralRecord?.workflowReferenceName,
          data: dataWorkflowInstance,
          dataSchema: dataSchema,
        };
        await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
      } else {
        await handlerGenericStepWorkflow(
          workflowInstance,
          referralRecord,
          payloadForUpdateWorkflowinstance,
          checklistData,
          userEmail,
          modifiedFields
        );
      }
    } catch (err) {
      console.log('error on saveSelectedAddress', err);
    } finally {
      fetchReferralRecord(referralId);
      toggleModal('fixAddress', false);
    }
  };

  const saveCountyAddress = async (updatedIsInCountyPayload: any) => {
    const modifiedFields = {
      isInCounty: updatedIsInCountyPayload?.county === 'in' ? true : false,
      Notes: updatedIsInCountyPayload?.notes,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
      handleSetRejectCondition(updatedIsInCountyPayload?.county);
    } catch (err) {
      console.log('error on saveSelectedAddress', err);
    } finally {
      fetchReferralRecord(referralId);
      toggleModal('addressInCounty', false);
      setReject(false);
      setRejectCounty(false);
      setRejectExternal(false);
    }
  };

  const handleSetRejectCondition = async (data: any) => {
    if (data === 'out' || isEmpty(data)) {
      const confirms = await confirmDialog({
        title: 'Warning',
        description: (
          <>
            <Typography gutterBottom>¡The address is out the county!</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Do you want to reject this referral?
            </Typography>
          </>
        ),
      });

      if (confirms) {
        setRejectCounty(true);
        setRejectExternal(true);
      }
    }
  };

  const handleConfirmAddressInCounty = async (data: any) => {
    patient!.address! = data;
    await updatePatient(patient!);
    await refreshPatient();
  };

  const handleReAssingOwned = async (ownedSelected: string) => {
    try {
      const payload = {
        record: {
          ...workflowInstance,
          assigned: {
            selectedUser: ownedSelected,
          },
        },
      };

      await WorkflowService.reAssignOwner(payload);
      fetchStageFromWorkflow();
    } catch (err) {
      console.log('error on handleReAssingOwned');
    }
  };

  const getPaginateQueryByPatient = () => {
    const paginationQuery: PaginateQuery = {};
    paginationQuery.filter = {
      indexedSubjectFhirRefUri: `${configurations?.fhir?.baseUrl}/Patient/${patientId}`,
      indexedStatus: 'active',
    };
    return paginationQuery;
  };

  const getCaseListByPatient = async () => {
    const cases = await CaseService.getAll(getPaginateQueryByPatient());
    if (patientId && cases) {
      setCaseDataByPatient(cases);
    }
  };

  const handlerConfirmGuarantor = async (data: any) => {
    try {
      let result;
      if (!data?.skip) {
        if (data?.id) {
          result = await updateRelatedPerson(data);
        } else {
          result = await createRelatedPerson(data);
        }
      }

      let modifiedFields: any;

      if (result && !data?.skip) {
        modifiedFields = {
          fhirReference: mapFhirResourceReference(result),
          source: 'workflow',
        };
      }

      const payloadForUpdateWorkflowinstance = {
        referenceValue: referralId,
        referenceName: referralRecord?.workflowReferenceName,
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      fetchReferralRecord(referralId);
    }
    refreshRelatedPerson();
  };

  const handlerConsentInformation = async (data: any) => {
    setRefreshConsents(true);

    const modifiedFields = {
      consents: data,
    };

    const additionalParams = patient ? { patientId: patient?.id } : null;

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
      setRefreshConsents(false);
    } finally {
      fetchReferralRecord(referralId);
    }
  };

  const handlerProvidersInformation = async (data: PayloadProvider) => {
    setIsLoading(true);
    try {
      const { generalPractitioner, workflowDataScope } = data;
      if (generalPractitioner && generalPractitioner.length > 0) {
        await updatePatient(
          produce(patient!, (draft) => {
            draft.generalPractitioner = generalPractitioner.map((e) => ({ reference: e }));
          })
        );
      }

      const modifiedFields = {
        providers: workflowDataScope,
      };

      const additionalParams = patient ? { patientId: patient?.id } : null;

      const payloadForUpdateWorkflowinstance = {
        referenceValue: referralId,
        referenceName: referralRecord?.workflowReferenceName,
      };

      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields,
        additionalParams
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      fetchReferralRecord(referralId);
      setIsLoading(false);
    }
    refreshPractitionerRoles();
    refreshPatient();
  };

  const handleOpenFromIsCounty = () => toggleModal('fixAddress', true);

  const handleUpdateReferral = async (data: ServiceRequest) => {
    const id = data?.id;
    const isEditing = Boolean(id);

    let result: any = {};

    if (isEditing) {
      result = await updateServiceRequest(
        produce(serviceRequest!, (draft) => {
          draft.supportingInfo = data.supportingInfo;
          draft.note = data.note;
        })
      );
    }

    refreshServiceRequest();

    return result;
  };

  const handlerConfirmGeneric = async (data?: any) => {
    const modifiedFields = {
      ...data,
    };

    const payloadForUpdateWorkflowinstance = {
      referenceValue: referralId,
      referenceName: referralRecord?.workflowReferenceName,
    };

    try {
      await handlerGenericStepWorkflow(
        workflowInstance,
        referralRecord,
        payloadForUpdateWorkflowinstance,
        checklistData,
        userEmail,
        modifiedFields
      );
    } catch (err) {
      console.log('error', err);
    } finally {
      fetchReferralRecord(referralId);
    }
  };

  const onUpdateIdentifiers = useCallback(
    async (newIdentifiers: Identifier[]) => {
      await updateServiceRequest(
        produce(serviceRequest!, (draft) => {
          draft.identifier = newIdentifiers;
        })
      );
    },
    [serviceRequest]
  );

  const isAllLoading = isPractitionerRoleFetching || isLoading || refreshConsents;

  const taskPermissions: any = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.TASKS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.TASKS.EDIT] }),
    }),
    []
  );

  const {
    openAssessmentFrame,
    selectedTaskAssessmentUrl,
    handleAssessmentSelect,
    onCancelAssessment,
  } = useTaskAssessment(tasks, refreshTaskList);

  const refreshEntities = useCallback(() => {
    refreshTaskList();
  }, []);

  useEffect(() => {
    onEvent(CREATE_TASK, refreshEntities);
    onEvent(UPDATE_TASK, refreshEntities);
    return () => {
      removeEvent(CREATE_TASK, refreshEntities);
      removeEvent(UPDATE_TASK, refreshEntities);
    };
  }, []);

  const handlerComplementaryInfo = async (store: any) => {
    if (store.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM) {
      const originalAddress = mergeLineAddress(
        store.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM?.modifiedFields
      );
      store.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM.modifiedFields = { ...originalAddress };
    }

    const payloadForUpdateWorkflowinstance = {
      referenceValue: workflowInstance?.referenceValue,
      referenceName: workflowInstance?.referenceName,
      data: JSON.stringify({
        scope: {
          checklistItem: {
            ...store,
          },
        },
      }),
    };

    try {
      await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
    } catch (err) {
      console.log('error on handleComplementaryInfo', err);
    }
  };

  const defaultStructure = useMemo(() => {
    const checklistItem = getParseColumn(workflowInstance, 'data')?.scope?.checklistItem;

    const addressFormated = getAddressFormated(
      checklistItem?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM?.modifiedFields
    );

    return {
      ...(checklistItem?.VALIDATE_ADDRESS_IN_COUNTY_REFERRAL_ITEM?.validatedOn && {
        VALIDATE_ADDRESS_IN_COUNTY_REFERRAL_ITEM: {
          validatedOn: moment
            .utc(new Date(checklistItem?.VALIDATE_ADDRESS_IN_COUNTY_REFERRAL_ITEM?.validatedOn))
            .format('MM/DD/YYYY'),
        },
      }),
      ...(checklistItem?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM && {
        VALIDATE_HOME_ADDRESS_REFERRAL_ITEM: {
          ...(checklistItem?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM?.validatedOn && {
            validatedOn: moment
              .utc(new Date(checklistItem?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM?.validatedOn))
              .format('MM/DD/YYYY'),
          }),
          modifiedFields: {
            ...(checklistItem?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM?.modifiedFields && {
              ...addressFormated,
            }),
          },
        },
      }),
      ...(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM && {
        CONTACT_CLIENT_REFERRAL_ITEM: {
          ...(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.validatedOn && {
            validatedOn: moment
              .utc(new Date(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.validatedOn))
              .format('MM/DD/YYYY'),
          }),
          modifiedFields: {
            ...(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.modifiedFields?.contactOn && {
              contactOn: moment
                .utc(
                  new Date(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.modifiedFields?.contactOn)
                )
                .format('MM/DD/YYYY'),
            }),
            ...(checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.modifiedFields?.nextContactOn && {
              nextContactOn: moment
                .utc(
                  new Date(
                    checklistItem?.CONTACT_CLIENT_REFERRAL_ITEM?.modifiedFields?.nextContactOn
                  )
                )
                .format('MM/DD/YYYY'),
            }),
          },
        },
      }),
      ...(checklistItem?.CONTACT_PCP_REFERRAL_ITEM && {
        CONTACT_PCP_REFERRAL_ITEM: {
          ...(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.validatedOn && {
            validatedOn: moment
              .utc(new Date(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.validatedOn))
              .format('MM/DD/YYYY'),
          }),
          modifiedFields: {
            ...(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.modifiedFields?.contactOn && {
              contactOn: moment
                .utc(new Date(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.modifiedFields?.contactOn))
                .format('MM/DD/YYYY'),
            }),
            ...(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.modifiedFields?.nextContactOn && {
              nextContactOn: moment
                .utc(
                  new Date(checklistItem?.CONTACT_PCP_REFERRAL_ITEM?.modifiedFields?.nextContactOn)
                )
                .format('MM/DD/YYYY'),
            }),
          },
        },
      }),
    };
  }, [workflowInstance]);

  const referralPermissions = useMemo(
    () => ({
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.EDIT] }),
      isAllowedToViewConsent:
        checkAclValidation({ acls: [crsAcls.CRS.REFERRAL.CONSENT.VIEW] }) &&
        metadataWorkflow?.consentGrid,
      isAllowedAddContactAttempt: checkAclValidation({
        acls: [crsAcls.CRS.REFERRAL.CONTACT_ATTEMPTS.ADD],
      }),
    }),
    [metadataWorkflow]
  );

  const handleGetTasks = async () => {
    try {
      const query = `${apiRoutes.getFhirTask}?subject=${patient?.id}&based-on=ServiceRequest/${serviceRequest?.id}`;
      const response: any = await fhirClient.search(query);
      if (response?.entry && isArray(response?.entry) && response?.entry?.length > 0) {
        setServiceRequestTasks(response?.entry?.map((x) => x?.resource));
      }
    } catch (error) {
      console.log(error);
      setServiceRequestTasks([]);
    }
  };

  useEffect(() => {
    if (patient && serviceRequest) {
      handleGetTasks();
    }
    if (!patient || !serviceRequest) {
      setServiceRequestTasks([]);
    }
  }, [patient, serviceRequest]);

  const handleSaveEditTask = async (data: any) => {
    await handleGetTasks();
    if (modals.orderDevice) {
      toggleModal('orderDevice', false);
    } else if (modals.setupDevice) {
      toggleModal('setupDevice', false);
    }
  };

  const rpmOrderDeviceTask = useMemo(() => {
    const found = filterTasksByDefinition(
      serviceRequestTasks,
      configurations?.fhir?.activityDefinitionRpmOrderDevice
    );
    if (found.length > 0) return found[0];
  }, [serviceRequestTasks, configurations]);

  const rpmSetupDeviceTask = useMemo(() => {
    const found = filterTasksByDefinition(
      serviceRequestTasks,
      configurations?.fhir?.activityDefinitionRpmSetupDevice
    );
    if (found.length > 0) return found[0];
  }, [serviceRequestTasks, configurations]);

  const titleFromWorkflowInstance = useMemo(
    () => (workflowInstance?.workFlow?.name ? `${workflowInstance?.workFlow?.name} Details` : ''),
    [workflowInstance]
  );

  return (
    <Page title={titleFromWorkflowInstance}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title={titleFromWorkflowInstance}
          heading=""
          links={[
            { name: `${i18n('admin.list.dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${i18n('breadcrumbs.title', 'crs')}` },
            { name: `${i18n('referral.list.title', 'crs')}`, href: PATH_DASHBOARD.crs.referral },
            { name: titleFromWorkflowInstance },
          ]}
          action={
            <Grid container direction={'row'}>
              {metadataWorkflow?.deviceManagementLink && (
                <Grid item xs={6}>
                  <ExternalLink
                    externalLinkKeysForData={['deviceManagementLink']}
                    patient={patient}
                  />
                </Grid>
              )}

              {metadataWorkflow?.syncReferral && (
                <Grid item xs={6}>
                  <LoadingButton
                    sx={{ float: 'right', height: '100%' }}
                    variant="contained"
                    disabled={syncing || !serviceRequest?.id}
                    onClick={handleSyncReferral}
                    loading={syncing || !serviceRequest?.id}
                  >
                    {i18n('referral.details.syncReferral', 'crs')}
                  </LoadingButton>
                </Grid>
              )}
            </Grid>
          }
        />
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <ReferralWorkflowComp
              data={referralWorkflowData}
              refreshRecord={fetchStageFromWorkflow}
              initialWorkflow={initialWorkflow}
              workflowHandler={WorkflowHandler}
              workflowData={WorkflowData}
              onTransitionSuccess={handleTransitionSuccess}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <Typography sx={{ mt: 1.5 }} variant="overline">
                collapse all
              </Typography>
              <IconButton onClick={() => setOpenCollapse(!openCollapse)}>
                <Iconify
                  icon={openCollapse ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                />
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ReferralSummary
              referralRecord={referralRecord}
              serviceRequest={serviceRequest}
              title={'Referral Summary'}
              workflowInstance={workflowInstance}
              users={allUsers}
              onOwnedAssign={handleReAssingOwned}
              fhirPatient={fhirPatientId}
              handlers={{
                insuranceHandler: () => toggleModal('insuranceInfo', true),
                progressNotesHandler: () => toggleModal('missingProgressNotes', true),
                labNotesHandler: () => toggleModal('missingLabNotes', true),
                consentHandler: () => toggleModal('consentForm', true),
              }}
              referralWorkflowData={referralWorkflowData}
              openCollapseExternal={openCollapseReferralSummary}
              children={null}
              healthCareServices={healthCareServices}
              updateServiceRequest={updateServiceRequest}
              isAllowedToEdit={referralPermissions.isAllowedToEdit}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ExpandableCard
              title={'Referral Complementary Info'}
              openCollapseExternal={openCollapse}
            >
              <ComplementaryInfo
                dataSchema={getParseColumn(workflowInstance, 'dataSchema')}
                data={getParseColumn(workflowInstance, 'data')}
                defaultStructure={defaultStructure}
                checklistItemModified={{
                  VALIDATE_HOME_ADDRESS_REFERRAL_ITEM:
                    defaultStructure?.VALIDATE_HOME_ADDRESS_REFERRAL_ITEM,
                }}
                handleComplementaryInfo={handlerComplementaryInfo}
                isAllowedToEdit={referralPermissions.isAllowedToEdit}
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            {patient && (
              <PatientSummary
                title={'Patient Information'}
                patient={patient}
                openCollapseExternal={openCollapse}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6} lg={6}>
            <ReferralIdentifiers
              serviceRequest={serviceRequest}
              isOpen={openCollapse}
              onUpdateIdentifiers={onUpdateIdentifiers}
            />
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard
              title={i18n('referral.details.secondaryReferrals.title', 'crs')}
              openCollapseExternal={openCollapse}
            >
              <ReferralHistory serviceRequest={serviceRequest} patient={patient} />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard
              title={i18n('referral.details.inmmunizations.title', 'crs')}
              openCollapseExternal={openCollapse}
            >
              <TableCustom
                data={mapImmunizationsToDisplay(immunizations)}
                tableHead={translateTableHead(TABLE_HEAD_IMMUNIZATIONS, i18n, 'crs')}
                handleOpen={() => toggleModal('immunization', true)}
                titleButton={i18n('referral.details.inmmunizations.button', 'crs')}
                statusList={STATUS_OPTION_IMMUNIZATION}
              />
            </ExpandableCard>
          </Grid>

          {metadataWorkflow?.deviceRequestsList &&
          checkAclValidation({ acls: [crsAcls.CRS.PATIENT.DEVICE.VIEW] }) ? (
            <Grid item xs={12}>
              <ExpandableCard
                title={i18n('patients.details.device.title', 'crs')}
                openCollapseExternal={openCollapse}
              >
                <DeviceRequestsList
                  useExternalQuery={true}
                  patient={patient}
                  serviceRequest={serviceRequest}
                  permissions={{
                    isAllowedAdd: checkAclValidation({
                      acls: [crsAcls.CRS.PATIENT.OBSERVATION.ADD],
                    }),
                    isAllowedEdit: checkAclValidation({
                      acls: [crsAcls.CRS.PATIENT.OBSERVATION.EDIT],
                    }),
                  }}
                />
              </ExpandableCard>
            </Grid>
          ) : null}

          {/* <Grid item xs={12}>
            <Card>
              <Typography variant="subtitle1" sx={{ m: 2 }}>
                Cases
              </Typography>
              <SummaryTable header={TABLE_HEAD_CASES} data={dataCase} />
            </Card>
          </Grid> */}

          <Grid item xs={12}>
            <ExpandableCard
              title={i18n('referral.details.contactAttempts.title', 'crs')}
              openCollapseExternal={openCollapse}
            >
              <TableCustom
                data={Array.isArray(contactAttempt) && mapContactAttemptsReferral(contactAttempt)}
                tableHead={translateTableHead(TABLE_HEAD_CONTACT, i18n, 'crs')}
                childrenButtons={
                  <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                    {metadataWorkflow?.contactClientAdd && [
                      <Button
                        key="contact-client"
                        onClick={() => toggleModal('contactClientForm', true)}
                      >
                        Contact Client
                      </Button>,
                      <Button key="contact-pcp" onClick={() => toggleModal('contactPCPForm', true)}>
                        Contact PCP
                      </Button>,
                    ]}
                  </Grid>
                }
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard
              title={i18n('referral.details.assessmentsForm.title', 'crs')}
              openCollapseExternal={openCollapse}
            >
              <TableCustom
                data={Array.isArray(assessmentsForms) && assessmentsForms}
                tableHead={translateTableHead(TABLE_HEAD_ASSESSMENTS, i18n, 'crs')}
                childrenButtons={
                  <Grid item xs={4} display={'flex'} justifyContent={'flex-end'}>
                    {checkAclValidation({
                      acls: [crsAcls.CRS.PATIENT.ASSESSMENTS.START],
                    }) && (
                      <StartAssessmentsButton
                        keyRegistry="crs-patient-assessments"
                        patientId={patient?.id ?? ''}
                        additionalQueryParams={`&referralUUID=${referralId}`}
                      />
                    )}
                  </Grid>
                }
              />
            </ExpandableCard>
          </Grid>

          {referralPermissions.isAllowedToViewConsent && (
            <Grid item xs={12}>
              <ExpandableCard
                title={i18n('patients.details.consents.title', 'crs')}
                openCollapseExternal={openCollapse}
              >
                <ConsentGrid patient={patient} />
              </ExpandableCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <ExpandableCard title={i18n('Consent Form', 'crs')} openCollapseExternal={openCollapse}>
              <TableCustom
                data={mapCommunicationsToConsentFormDisplay(communications, 'consent_form')}
                tableHead={translateTableHead(TABLE_HEAD_CONSENT_FORM, i18n, 'crs')}
                statusList={STATUS_OPTION_IMMUNIZATION}
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard
              title={i18n('referral.details.notesDetails.title', 'crs')}
              openCollapseExternal={openCollapse}
            >
              <NotesDisplayWithAdd
                data={mapCommunicationsToNotesDisplay(communications, [
                  'notes_referral',
                  'mint_referral_notes',
                  'notes_member_engagement',
                ])}
                columns={CRS_NOTES_COLUMNS}
                titleButton={i18n('referral.details.notesDetails.button', 'crs')}
                isAllowedToAdd={true}
                onAddNote={() => toggleModal('note', true)}
                emptyMessage="No notes available"
              />
            </ExpandableCard>
          </Grid>

          <Grid item xs={12}>
            <ExpandableCard title={i18n('patients.details.attachments.title', 'crs')}>
              <Card style={{ boxShadow: 'none', position: 'static' }}>
                <DocumentList
                  patient={patient}
                  documentReferences={
                    isDocumentsLoading || !(documentReferencesQuery as any)?.related
                      ? []
                      : documentReferences
                  }
                  resource={serviceRequest ?? undefined}
                  refreshDocumentReference={refreshDocumentReference}
                  openCollapseExternal={openCollapse}
                  handleUpdateResource={handleUpdateReferral}
                />
              </Card>
            </ExpandableCard>
          </Grid>
        </Grid>

        <EditTaskDialog
          patient={patient}
          serviceRequest={serviceRequest}
          task={rpmOrderDeviceTask}
          open={modals.orderDevice}
          onClose={() => toggleModal('orderDevice', false)}
          handleSave={handleSaveEditTask}
        />

        <EditTaskDialog
          patient={patient}
          serviceRequest={serviceRequest}
          task={rpmSetupDeviceTask}
          open={modals.setupDevice}
          onClose={() => toggleModal('setupDevice', false)}
          handleSave={handleSaveEditTask}
        />

        {configurations && (
          <FixAddressDialog
            isOpen={modals.fixAddress}
            handleClose={() => toggleModal('fixAddress', false)}
            handleSave={handleFixAddress}
            saveSelectedAddress={saveSelectedAddress}
            workflowInstanceData={workflowInstance}
            addressesList={patient?.address}
            addAddressTab={modals.addAddressTab}
            handleAddAddressOpen={(val: boolean) => toggleModal('addAddressTab', val)}
            configurations={configurations}
          />
        )}

        <AddressInCountyDialog
          isOpen={modals.addressInCounty}
          handleClose={() => toggleModal('addressInCounty', false)}
          handleSave={handleConfirmAddressInCounty}
          workflowInstance={workflowInstance}
          saveCountyAddress={saveCountyAddress}
          addressList={patient?.address}
          handleOpenFromIsCounty={handleOpenFromIsCounty}
        />

        <ImmunizationAddForm
          open={modals.immunization}
          onCancel={() => toggleModal('immunization', false)}
          handleCreateImmunization={handleCreateImmunization}
          patient={patient}
        />

        <AssessmentFromChecklistData
          patient={patient}
          checkListData={checklistData}
          open={modals.assessment}
          onClose={() => {
            toggleModal('assessment', false);
          }}
          handlerSave={handlerConfirmGeneric}
          basedOn={{ reference: `${serviceRequest?.resourceType}/${serviceRequest?.id}` }}
        />

        <NoteAddForm
          open={modals.note}
          patient={patient}
          onCancel={() => toggleModal('note', false)}
          resource={[serviceRequest]}
          handleCreate={handleCreateCommunicationFhir}
          typeNote={'notes_referral'}
        />

        <InsuranceInfo
          patient={patient}
          serviceRequest={serviceRequest}
          open={modals.insuranceInfo}
          onCancel={() => toggleModal('insuranceInfo', false)}
          workflowReferral={true}
          handleInsuranceInfo={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <MissingNotes
          option="Link Progress Notes"
          patient={patient}
          serviceRequest={serviceRequest}
          open={modals.missingProgressNotes}
          onCancel={() => toggleModal('missingProgressNotes', false)}
          workflowReferral={true}
          handleNotes={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <MissingNotes
          option="Link Lab Notes"
          patient={patient}
          serviceRequest={serviceRequest}
          open={modals.missingLabNotes}
          onCancel={() => toggleModal('missingLabNotes', false)}
          workflowReferral={true}
          handleNotes={handleMarkItemCompleted}
          handleUpdateResource={handleUpdateReferral}
        />

        <ConsentForm
          patient={patient}
          communication={communications}
          serviceRequest={serviceRequest}
          handleCreateCommunication={handleCreateCommunication}
          open={modals.consentForm}
          onCancel={() => toggleModal('consentForm', false)}
        />

        <ContactForm
          option={i18n('referral.details.contactAttempts.contactClientTitle', 'crs')}
          lastRecord={lastContactClient}
          countRecord={countContactClient}
          referral={referralRecord}
          open={modals.contactClientForm}
          onCancel={() => toggleModal('contactClientForm', false)}
          handleCreateContactForm={handleCreateContactForm}
          assessmentsLinks={assessmentsLinks}
          patientId={patientId}
        />

        <ContactForm
          option={i18n('referral.details.contactAttempts.documentPcp', 'crs')}
          lastRecord={lastContactPCP}
          countRecord={countContactPCP}
          referral={referralRecord}
          open={modals.contactPCPForm}
          onCancel={() => toggleModal('contactPCPForm', false)}
          handleCreateContactForm={handleCreateContactForm}
        />
      </Container>

      <ConfirmMemberInfo
        isOpen={modals.confirm}
        handlerIsOpen={(val: boolean) => toggleModal('confirm', val)}
        patient={patient}
        handlePatient={updatePatient}
        handleConfirmMember={handlerConfirmGeneric}
        refreshPatient={refreshPatient}
      />

      <ConfirmGuarantorInfo
        patient={patient}
        isOpen={modals.confirmGuarantor}
        handlerIsOpen={(val: boolean) => toggleModal('confirmGuarantor', val)}
        relatedPerson={relatedPersons?.length ? relatedPersons[0] : null}
        handleRelatedPerson={handlerConfirmGuarantor}
      />

      <ConsentInformation
        patient={patient}
        isLoading={isAllLoading}
        workflowInstanceData={getParseColumn(workflowInstance, 'data')}
        handlerSave={handlerConsentInformation}
        open={modals.consents}
        consentOption={intakeOption?.consents}
        consents={consents}
        breadcrumbs={[
          `${i18n('referral.details.title', 'crs')}`,
          `${i18n('patientEngagement.details.workflow.step.intake', 'engagement')}`,
        ]}
        onClose={() => toggleModal('consents', false)}
      />

      <ProviderInformation
        patient={patient}
        careTeams={[]}
        relatedProviders={relatedProviders}
        providerOption={intakeOption?.providers}
        open={modals.provideInformation}
        isLoading={isAllLoading}
        workflowInstanceData={getParseColumn(workflowInstance, 'data')}
        onClose={() => toggleModal('provideInformation', false)}
        handlerSave={handlerProvidersInformation}
      />

      <CaseSelectionDialog
        open={modals.caseSelection}
        onClose={() => toggleModal('caseSelection', false)}
        patient={patient}
        onCaseSelected={handleCaseSelected}
        configurations={configurations}
      />

      {selectedTaskAssessmentUrl && openAssessmentFrame ? (
        <AssessmentFrame
          open={openAssessmentFrame}
          url={selectedTaskAssessmentUrl}
          onCancel={onCancelAssessment}
        />
      ) : null}
    </Page>
  );
});

export default ReferralDetails;
